import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Modal } from '@/nova/components';
import type { StoryboardBeatInput, StoryboardBeatRole, VideoAspectRatio } from '@/store/slices/creativeJobs';
import type { Translate } from '../../../video-composer';
import { aspectRatioValue } from '../../../aspect-ratios';

import {
  Body,
  FootHint,
  Frame,
  MetaLabel,
  MetaList,
  MetaRow,
  MetaValue,
  Placeholder,
  PlaceholderText,
  StillImg,
  Video,
} from './BeatModal.styles';

/** The translated label for a beat's dramatic role (mirrors the beat node's role select). */
const roleLabel = (role: StoryboardBeatRole, t: Translate): string =>
  role === 'hook' ? t('Hook') : role === 'offer' ? t('Offer') : role === 'proof' ? t('Proof') : t('Call to action');

export type BeatModalProps = {
  /** The 1-based beat to show, or null when the modal is closed. */
  beatIndex: number | null;
  /** The draft beats, indexed by `beatIndex - 1`. */
  beats: StoryboardBeatInput[];
  /** Index-aligned clip urls from the job (`segments[j]` = beat j's clip). */
  segments?: string[];
  /** Index-aligned end-frame images from the job (`frames[j]` = beat j's end frame): the clip's poster,
   *  and the fallback still shown until the clip exists. */
  frames?: string[];
  aspect: VideoAspectRatio;
  /** Whether a run is in flight, so the footer Regenerate is disabled (the action self-gates too). */
  running?: boolean;
  /** Video renders are blocked (a mature primary art style): Regenerate is disabled, with a note beside it. */
  blocked?: boolean;
  /** Start a regenerate/resume from this beat (1-based); the footer button also closes the modal. */
  onRegenerate: (beatIndex: number) => void;
  onClose: () => void;
};

/**
 * A preview dialog for a single storyboard beat: its rendered clip (or the still end frame, or a
 * "not generated yet" placeholder), plus its role / motion / duration, and a Regenerate action that
 * re-runs the ad from this beat. Built on the shared {@link Modal}. `beatIndex` is nullable so the last
 * shown beat stays rendered through Modal's keep-mounted exit transition (held in `shownRef`).
 */
export const BeatModal = ({
  beatIndex,
  beats,
  segments,
  frames,
  aspect,
  running,
  blocked = false,
  onRegenerate,
  onClose,
}: BeatModalProps): React.ReactElement => {
  const { t } = useTranslation();

  // Keep the last opened beat so the body still renders while the modal animates out.
  const shownRef = React.useRef(1);
  React.useEffect(() => {
    if (beatIndex != null) shownRef.current = beatIndex;
  }, [beatIndex]);

  const idx = beatIndex ?? shownRef.current;
  const beat: StoryboardBeatInput | undefined = beats[idx - 1];
  const ratio = aspectRatioValue(aspect);
  const clipUrl = segments?.[idx] || undefined;
  const stillUrl = frames?.[idx] || undefined;

  const handleRegenerate = (): void => {
    if (running || blocked) return;
    onRegenerate(idx);
    onClose();
  };

  return (
    <Modal
      open={beatIndex != null}
      title={t('Beat {{n}}', { n: idx })}
      onClose={onClose}
      footer={
        <>
          {blocked ? <FootHint text="Mature content isn't compatible with video generation." /> : null}
          <Button
            variant="primary"
            icon="refresh"
            label={t('Regenerate')}
            disabled={running || blocked}
            onClick={handleRegenerate}
          />
        </>
      }
    >
      <Body>
        {clipUrl ? (
          <Frame $ratio={ratio}>
            <Video controls playsInline preload="metadata" src={clipUrl} poster={stillUrl} />
          </Frame>
        ) : stillUrl ? (
          <Frame $ratio={ratio}>
            <StillImg src={stillUrl} alt="" />
          </Frame>
        ) : (
          <Placeholder $ratio={ratio}>
            <Icon name="video" size={22} />
            <PlaceholderText text="This scene has not been generated yet." />
          </Placeholder>
        )}

        {beat ? (
          <MetaList>
            <MetaRow>
              <MetaLabel text="Role" />
              <MetaValue raw text={roleLabel(beat.role, t)} />
            </MetaRow>
            <MetaRow>
              <MetaLabel text="Motion" />
              <MetaValue raw text={beat.motion_intent} />
            </MetaRow>
            <MetaRow>
              <MetaLabel text="Duration" />
              <MetaValue text="{{n}} seconds" placeholders={{ n: beat.duration_seconds }} />
            </MetaRow>
          </MetaList>
        ) : null}
      </Body>
    </Modal>
  );
};

export default BeatModal;
